import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Users, Star, Heart, ChevronDown, Filter, Search, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ImageWithLoader from '../components/ImageWithLoader';
import { experiencesData } from '../data/experiencesData';

const AllExperiences = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 3000]);
  const [durationFilter, setDurationFilter] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { t } = useTranslation();
  
  // Filter experiences based on selected filters
  const filteredExperiences = experiencesData.filter(exp => {
    // Category filter
    const categoryMatch = selectedCategory === 'All' || exp.category === selectedCategory;
    
    // Search query filter
    const searchMatch = exp.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                        exp.location.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Price range filter
    const priceMatch = exp.price >= priceRange[0] && exp.price <= priceRange[1];
    
    // Duration filter
    const durationMatch = durationFilter.length === 0 || 
                          (durationFilter.includes('short') && exp.duration.includes('hour')) ||
                          (durationFilter.includes('medium') && exp.duration.includes('1 day')) ||
                          (durationFilter.includes('long') && (exp.duration.includes('2 day') || exp.duration.includes('3 day') || exp.duration.includes('4 day')));
    
    return categoryMatch && searchMatch && priceMatch && durationMatch;
  });

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Set document title
    document.title = t('pages.allExperiences.title') + ' | TravelCord';
  }, [t]);
  
  const categories = [
    'All', 'Wildlife', 'Safari', 'Cultural', 'Adventure', 'Birding'
  ];
  
  const handleDurationToggle = (duration: string) => {
    if (durationFilter.includes(duration)) {
      setDurationFilter(durationFilter.filter(d => d !== duration));
    } else {
      setDurationFilter([...durationFilter, duration]);
    }
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('common.back')}
            </Link>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{t('pages.allExperiences.title')}</h1>
          <p className="text-lg text-white/90 max-w-3xl">
            {t('pages.allExperiences.subtitle')}
          </p>
        </div>
      </section>
      
      {/* Filters Section */}
      <section className="bg-white border-b border-gray-200 sticky top-14 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Search Bar */}
            <div className="relative w-full md:w-auto md:flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-ta-green focus:border-ta-green sm:text-sm py-2"
              />
            </div>
            
            {/* Category Pills */}
            <div className="hidden md:flex overflow-x-auto scrollbar-hide space-x-2">
              {categories.map((category, index) => (
                <button
                  key={index}
                  className={`px-3 py-1.5 rounded-full whitespace-nowrap transition-all duration-300 text-sm ${
                    category === selectedCategory 
                      ? 'bg-ta-green text-white shadow-sm' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {t(`experiences.categories.${category.toLowerCase()}`)}
                </button>
              ))}
            </div>
            
            {/* Filter Button */}
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Filter className="h-4 w-4 mr-1" />
              {t('pages.allExperiences.filters')}
              {isFilterOpen ? (
                <ChevronDown className="ml-1 h-4 w-4 rotate-180 transition-transform" />
              ) : (
                <ChevronDown className="ml-1 h-4 w-4 transition-transform" />
              )}
            </button>
          </div>
          
          {/* Expanded Filters */}
          {isFilterOpen && (
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Category Filter (Mobile) */}
              <div className="md:hidden">
                <h3 className="text-sm font-medium text-gray-900 mb-2">{t('experiences.categories.all')}</h3>
                <div className="flex flex-wrap gap-2">
                  {categories.map((category, index) => (
                    <button
                      key={index}
                      className={`px-3 py-1.5 rounded-full whitespace-nowrap transition-all duration-300 text-sm ${
                        category === selectedCategory 
                          ? 'bg-ta-green text-white shadow-sm' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      onClick={() => setSelectedCategory(category)}
                    >
                      {t(`experiences.categories.${category.toLowerCase()}`)}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Price Range Filter */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">{t('pages.safariPackages.priceRange')}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>${priceRange[0]}</span>
                    <span>${priceRange[1]}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="3000"
                    step="50"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-ta-green"
                  />
                </div>
              </div>
              
              {/* Duration Filter */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">{t('pages.safariPackages.duration')}</h3>
                <div className="space-y-2">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={durationFilter.includes('short')}
                      onChange={() => handleDurationToggle('short')}
                      className="rounded border-gray-300 text-ta-green focus:ring-ta-green"
                    />
                    <span className="ml-2 text-sm text-gray-700">{t('common.short')} (1-4 {t('common.hours')})</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={durationFilter.includes('medium')}
                      onChange={() => handleDurationToggle('medium')}
                      className="rounded border-gray-300 text-ta-green focus:ring-ta-green"
                    />
                    <span className="ml-2 text-sm text-gray-700">{t('common.medium')} (1 {t('common.day')})</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={durationFilter.includes('long')}
                      onChange={() => handleDurationToggle('long')}
                      className="rounded border-gray-300 text-ta-green focus:ring-ta-green"
                    />
                    <span className="ml-2 text-sm text-gray-700">{t('common.long')} (2+ {t('common.days')})</span>
                  </label>
                </div>
              </div>
              
              {/* Rating Filter */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">{t('common.rating')}</h3>
                <div className="space-y-2">
                  {[4, 3, 2, 1].map((rating) => (
                    <label key={rating} className="inline-flex items-center">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-ta-green focus:ring-ta-green"
                      />
                      <span className="ml-2 text-sm text-gray-700 flex items-center">
                        {rating}+ <Star className="h-3 w-3 text-yellow-400 ml-1 fill-current" />
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      
      {/* Experiences Grid */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              {filteredExperiences.length} {t('common.experiencesFound')}
            </h2>
            <div className="flex items-center">
              <span className="text-sm text-gray-500 mr-2">{t('pages.allExperiences.sortBy')}:</span>
              <select className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-ta-green focus:border-ta-green">
                <option>{t('common.recommended')}</option>
                <option>{t('common.priceLowToHigh')}</option>
                <option>{t('common.priceHighToLow')}</option>
                <option>{t('common.rating')}</option>
                <option>{t('common.duration')}</option>
              </select>
            </div>
          </div>
          
          {filteredExperiences.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('pages.allExperiences.noResults')}</h3>
              <p className="text-gray-600 mb-4">{t('common.tryAdjustingFilters')}</p>
              <button
                onClick={() => {
                  setSelectedCategory('All');
                  setSearchQuery('');
                  setPriceRange([0, 3000]);
                  setDurationFilter([]);
                }}
                className="px-4 py-2 bg-ta-green text-white rounded-md hover:bg-ta-green-dark transition-colors"
              >
                {t('pages.allExperiences.resetFilters')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredExperiences.map((experience) => (
                <Link
                  key={experience.id}
                  to={`/experiences/${experience.id}`}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300"
                >
                  <div className="relative h-48">
                    <ImageWithLoader 
                      src={experience.image} 
                      alt={experience.title} 
                      className="w-full h-full object-cover"
                    />
                    <button className="absolute top-2 right-2 bg-white/80 p-1 rounded-full hover:bg-white transition-colors">
                      <Heart className="h-4 w-4 text-gray-600 hover:text-red-500" />
                    </button>
                    <div className="absolute bottom-2 left-2 bg-ta-green text-white text-xs px-2 py-0.5 rounded-sm">
                      {experience.category}
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center mb-1">
                      <div className="flex items-center text-ta-green">
                        <Star className="h-3 w-3 fill-current" />
                        <span className="ml-1 text-xs font-medium">{experience.rating}</span>
                      </div>
                      <span className="mx-1 text-gray-400 text-xs">•</span>
                      <span className="text-gray-600 text-xs">{experience.reviews} {t('common.reviews')}</span>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2">{experience.title}</h3>
                    <div className="flex items-center text-gray-600 mb-1">
                      <MapPin className="h-3 w-3 text-ta-green-dark" />
                      <span className="ml-1 text-xs">{experience.location}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-gray-600 mb-2">
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 text-ta-green-dark" />
                        <span className="ml-1">{experience.duration}</span>
                      </div>
                      <div className="flex items-center">
                        <Users className="h-3 w-3 text-ta-green-dark" />
                        <span className="ml-1">{experience.groupSize}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-[10px] text-gray-500">{t('common.from')}</p>
                        <p className="text-sm font-bold text-gray-900">${experience.price}</p>
                      </div>
                      <span className="py-1 px-2 text-xs bg-ta-green text-white rounded-md">
                        {t('experiences.viewDetails')}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      
      <Footer />
    </>
  );
};

export default AllExperiences;